const _ = require('lodash')
const {
  stringSpaceToDash,
  sqftObjectToString,
  booleanStringToBoolean,
  noBuyerPremiumConverter,
  propertyTypeConverter,
  assetTypeConverter,
  assetTypeConverterWithSpace,
  propertyTypeConverterWithSpace,
  auctionFormatConverterUpperCase,
  dateStringToUrl,
  listingTypeToLabels,
  sqftToLabel,
  checkOccupancy,
  numberWithPlus,
  categorizeLabels,
  addUrlKey,
} = require('../helpers/modelParameters')

/**
 * This schema helps to:
 *   - Map the different param naming convention between the platforms
 *   - Establish an easy to follow and manage conversion policy between
 *     the representation for the params on different platforms
 */
const schema = [
  /**
   * Location Parameters
   */
  {
    api: 'search',
    url: 'qs',
    converter: {
      api2url: _.identity,
    },
  },
  {
    label: 'State',
    api: 'property_state',
    url: 'state',
    converter: {
      api2url: _.identity,
      url2label: _.identity,
    },
  },
  {
    label: 'City',
    api: 'property_city',
    url: 'ct',
    converter: {
      api2url: stringSpaceToDash,
      url2label: _.identity,
    },
  },
  {
    label: 'County',
    api: 'property_county',
    url: 'county',
    converter: {
      api2url: stringSpaceToDash,
      url2label: _.identity,
    },
  },
  {
    label: 'Zip',
    api: 'property_zip',
    url: 'zp',
    converter: {
      api2url: _.identity,
      url2label: _.identity,
    },
  },
  {
    api: 'geo_location_box',
    url: 'bounds',
    converter: {
      api2url: _.identity,
    },
  },
  /**
   * Listing
   */
  {
    label: 'Listing Type',
    api: 'listing_type',
    url: 'lt',
    converter: {
      api2url: _.identity,
      url2label: listingTypeToLabels,
    },
  },
  {
    label: 'Auction Type',
    api: 'asset_event_type',
    url: 'af',
    converter: {
      api2url: _.identity,
      url2label: auctionFormatConverterUpperCase,
    },
  },

  /**
   * Asset Info
   */
  {
    label: 'Property Type',
    api: 'usecode_property_type',
    url: 'pt',
    converter: {
      api2url: propertyTypeConverter,
      url2label: propertyTypeConverterWithSpace,
    },
  },
  {
    label: 'Vacant',
    api: 'occ_status',
    url: 'os',
    converter: {
      api2url: _.identity,
      url2label: checkOccupancy,
    },
  },
  {
    label: 'Beds',
    api: 'beds_min',
    url: 'bd',
    converter: {
      api2url: _.identity,
      url2label: numberWithPlus,
    },
  },
  {
    label: 'Baths',
    api: 'baths_min',
    url: 'bt',
    converter: {
      api2url: _.identity,
      url2label: numberWithPlus,
    },
  },
  {
    label: 'Sqft Range',
    api: 'sqft_range',
    url: 'sqft',
    converter: {
      api2url: sqftObjectToString,
      url2label: sqftToLabel,
    },
  },

  /**
   * Sort
   */
  {
    api: 'sort',
    url: 'st',
    converter: {
      api2url: _.identity,
    },
  },

  /**
   * [SHOW ONLY]
   */
  {
    label: 'Financing Available',
    api: 'fin_avl',
    url: 'y_fa',
    converter: {
      api2url: booleanStringToBoolean,
      url2label: booleanStringToBoolean,
    },
  },
  {
    label: 'Interior Access Available',
    api: 'resi_ia',
    url: 'y_ia',
    converter: {
      api2url: booleanStringToBoolean,
      url2label: booleanStringToBoolean,
    },
  },
  {
    label: 'Reserve Disclosed',
    api: 'show_reserve',
    url: 'y_sr',
    converter: {
      api2url: booleanStringToBoolean,
      url2label: booleanStringToBoolean,
    },
  },
  {
    label: 'Broker Co-op',
    api: 'broker_coop_avail',
    url: 'y_bc',
    converter: {
      api2url: booleanStringToBoolean,
      url2label: booleanStringToBoolean,
    },
  },
  {
    label: "No Buyer's Premium",
    api: 'buyer_premium_avail',
    url: 'y_nbp',
    converter: {
      api2url: noBuyerPremiumConverter,
      url2label: noBuyerPremiumConverter,
    },
  },
  {
    label: 'Hide Ending Soon',
    api: 'hide_ending_soon',
    url: 'y_hes',
    converter: {
      api2url: booleanStringToBoolean,
      url2label: booleanStringToBoolean,
    },
  },

  /**
   * [SHOW ONLY] - DEPRECATED
   */
  {
    api: 'resi_cs',
    url: 'y_cs',
    converter: {
      api2url: booleanStringToBoolean,
    },
  },

  /**
   * [SHOW ONLY] - HIDDEN FEATURE
   */
  {
    api: 'global_search',
    url: 'global',
    converter: {
      api2url: _.identity,
    },
  },

  /**
   * Feed Properties
   */
  {
    api: 'distance',
    url: 'rd',
    converter: {
      api2url: _.identity,
    },
  },
  {
    api: 'location',
    url: 'lq',
    converter: {
      api2url: _.identity,
    },
  },

  /**
   * Other
   */
  {
    api: 'nearby_search',
    url: 'nbs',
    converter: {
      api2url: _.identity,
    },
  },
  {
    api: 'ending_soon_dt_all_min',
    url: 'dt',
    converter: {
      api2url: dateStringToUrl,
    },
  },

  /**
   * DEPRECATED
   */
  {
    label: 'Asset Type',
    api: 'usecode_product_type',
    url: 'at',
    converter: {
      api2url: assetTypeConverter,
      url2label: assetTypeConverterWithSpace,
    },
  },
  {
    api: 'oa',
    url: 'oa',
    converter: {
      api2url: _.noop,
    },
  },
]

/**
 * Param Model for transformations
 *   - Transform the object to the needed API or URL representation.
 */
class Filters {
  constructor(props) {
    this._params = Object.assign({}, props)
  }

  /*
   * Used with raw query params to get the api object
   */
  static getApiValueFromQueryParams(queryParams) {
    const apiParams = {}
    schema.map(param => {
      const urlParamValue = queryParams[param.url]
      if (urlParamValue) {
        apiParams[param.api] = urlParamValue
      }
    })
    return apiParams
  }

  /*
   * Used with raw query params to get the search url. The search app expects the url in a specific path configuration
   */
  static getUrlValueFromQueryParams(queryParams) {
    const urlParams = []
    schema.map(param => {
      const queryParamValue = queryParams[param.url]
      if (queryParamValue) {
        const urlParamValue = param.converter.api2url(queryParamValue)
        urlParams.push(addUrlKey(urlParamValue, param))
      }
    })
    return urlParams.join('/')
  }

  /**
   * Get params
   */
  get rawValue() {
    return this._params
  }

  get labels() {
    const labels = {
      mainDisplay: [],
      propertyAndAssetTypes: [],
      listAndAuctionTypes: [],
      showOnlyFields: [],
    }
    this._params['sqft_range'] = {
      min: _.get(this._params, 'sqft_min'),
      max: _.get(this._params, 'sqft_max'),
    }
    schema.map(param => {
      const apiParamValue =
        param.api === 'is_asset_online_event'
          ? _.toLower(this._params[param.api])
          : this._params[param.api]
      if (apiParamValue) {
        const labelParamValue =
          param.converter.url2label && param.converter.url2label(apiParamValue)
        if (labelParamValue)
          categorizeLabels(labels, param.label, labelParamValue)
      }
    })
    return labels
  }

  get urlValue() {
    const urlParams = []
    this._params['sqft_range'] = {
      min: _.get(this._params, 'sqft_min'),
      max: _.get(this._params, 'sqft_max'),
    }
    schema.map(param => {
      const apiParamValue =
        param.api === 'is_asset_online_event'
          ? _.toLower(this._params[param.api])
          : this._params[param.api]
      if (apiParamValue) {
        const urlParamValue = param.converter.api2url(apiParamValue)
        if (urlParamValue) urlParams.push(addUrlKey(urlParamValue, param))
      }
    })

    return urlParams.join('/')
  }
}

module.exports = Filters
